import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { useFormik } from "formik";
import {
  Button,
  Input,
  Select,
  Toggle,
  CheckBox,
  TopProgress,
} from "../../../../components";
import { useUi } from "../../../../context/ui.context";
import { currencyService } from "../../../../api";
import { extractErrorMessage } from "../../../../utils/error-extractor";
import {
  Section,
} from "../styles";
import styled from "styled-components";

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const RatesWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
  align-items: center;
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.grey[600]};
  font-size: 13px;
`;

const LastUpdatedText = styled(Muted)<{ stale?: boolean }>`
  color: ${({ theme, stale }) => (stale ? theme.errors[600] : theme.grey[600])};
  font-weight: ${({ stale }) => (stale ? 700 : 400)};
`;

const CurrencyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CurrencyRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  align-items: center;
  padding: 12px 16px;
  border: 1px solid ${({ theme }) => theme.grey[300]};
  border-radius: 10px;
  background: ${({ theme }) => theme.base.white};
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 20px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ theme }) => theme.palette[50]};
  color: ${({ theme }) => theme.palette[800]};
  border: 1px solid ${({ theme }) => theme.palette[200]};
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const ModalBody = styled.div`
  background: ${({ theme }) => theme.base.white};
  padding: 24px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.grey[300]};
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.08);
  min-width: 320px;
  max-width: 450px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
`;

const ModalTitle = styled.div`
  font-weight: 700;
  font-size: 18px;
  margin-bottom: 12px;
  color: ${({ theme }) => theme.palette[800]};
`;

type Currency = {
  code: string;
  rate: number;
  is_default?: boolean;
  is_used?: boolean;
};

type RatesConnectors = {
  auto_update: boolean;
  connector: string;
  all_connectors: string[];
  last_updated?: string;
};

const Currencies: FC = () => {
  const [t] = useTranslation();
  const { viewAlert } = useUi();

  const [loading, setLoading] = useState<boolean>(true);
  const [ratesLoading, setRatesLoading] = useState<boolean>(true);
  const [ratesSaving, setRatesSaving] = useState<boolean>(false);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [defaultCurrency, setDefaultCurrency] = useState<Currency | null>(null);
  const [createOptions, setCreateOptions] = useState<string[]>([]);
  const [ratesConnectors, setRatesConnectors] = useState<RatesConnectors>({
    auto_update: false,
    connector: "",
    all_connectors: [],
    last_updated: "",
  });

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [editing, setEditing] = useState<Currency | null>(null);
  const [savingCurrency, setSavingCurrency] = useState<boolean>(false);

  const baseCurrencySchema = useMemo(
    () =>
      yup.object().shape({
        code: yup.string().required(t("REQUIRED_FIELD")),
        rate: yup.number().typeError(t("MUST_BE_NUMBER")).positive(t("MUST_BE_POSITIVE")).required(t("REQUIRED_FIELD")),
        is_default: yup.bool(),
      }),
    [t]
  );

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    handleSubmit,
    resetForm,
    isValid,
  } = useFormik({
    enableReinitialize: true,
    validationSchema: baseCurrencySchema,
    initialValues: editing || { code: "", rate: 1, is_default: false },
    onSubmit: async (formValues: Currency) => {
      setSavingCurrency(true);
      try {
        if (editing) {
          await currencyService.update(formValues.code, formValues);
          viewAlert({
            severity: "success",
            message: t("CURRENCY_SAVED"),
          });
        } else {
          await currencyService.create(formValues);
          viewAlert({
            severity: "success",
            message: t("CURRENCY_CREATED"),
          });
        }
        setModalOpen(false);
        loadCurrencies();
      } catch (e: any) {
        viewAlert({
          severity: "error",
          message:
            e?.response?.data?.detail ||
            extractErrorMessage(e?.response?.data) ||
            t("ERROR_OCCURRED"),
        });
      } finally {
        setSavingCurrency(false);
      }
    },
  });

  const loadCurrencies = useCallback(async () => {
    setLoading(true);
    try {
      const res = await currencyService.list({ page_size: 50 });
      const objects: Currency[] = res?.data?.objects || res?.data || [];
      setCurrencies(objects);
      const def = objects.find((item) => item.is_default);
      setDefaultCurrency(def || null);
    } catch (e: any) {
      viewAlert({
        severity: "error",
        message:
          e?.response?.data?.detail ||
          extractErrorMessage(e?.response?.data) ||
          t("FAILED_TO_LOAD_DATA"),
      });
    } finally {
      setLoading(false);
    }
  }, [t, viewAlert]);

  const loadCreateOptions = useCallback(async () => {
    try {
      const res = await currencyService.getCreateOptions();
      const codes = res?.data?.codes || [];
      setCreateOptions(codes);
    } catch (e) {
      // silently ignore
    }
  }, []);

  const loadRatesConnectors = useCallback(async () => {
    setRatesLoading(true);
    try {
      const res = await currencyService.getRatesConnectors();
      const data = res?.data as RatesConnectors;
      setRatesConnectors((prev) => ({
        ...prev,
        ...data,
      }));
    } catch (e: any) {
      viewAlert({
        severity: "error",
        message:
          e?.response?.data?.detail ||
          extractErrorMessage(e?.response?.data) ||
          t("FAILED_TO_LOAD_DATA"),
      });
    } finally {
      setRatesLoading(false);
    }
  }, [t, viewAlert]);

  useEffect(() => {
    loadCurrencies();
    loadCreateOptions();
    loadRatesConnectors();
  }, [loadCurrencies, loadCreateOptions, loadRatesConnectors]);

  const handleDelete = async (currency: Currency) => {
    const confirmed = window.confirm(
      t("CONFIRM_DELETE", { code: currency.code })
    );
    if (!confirmed) return;
    try {
      await currencyService.delete(currency.code);
      viewAlert({
        severity: "success",
        message: t("CURRENCY_DELETED"),
      });
      loadCurrencies();
    } catch (e: any) {
      viewAlert({
        severity: "error",
        message:
          e?.response?.data?.detail ||
          extractErrorMessage(e?.response?.data) ||
          t("ERROR_OCCURRED"),
      });
    }
  };

  const openCreate = () => {
    resetForm();
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (currency: Currency) => {
    setEditing(currency);
    resetForm({ values: currency });
    setModalOpen(true);
  };

  const saveRatesConnectors = async () => {
    setRatesSaving(true);
    try {
      await currencyService.updateRatesConnectors({
        auto_update: ratesConnectors.auto_update,
        connector: ratesConnectors.connector,
      });
      viewAlert({
        severity: "success",
        message: t("SETTINGS_SAVED_SUCCESSFULLY"),
      });
      loadRatesConnectors();
    } catch (e: any) {
      viewAlert({
        severity: "error",
        message:
          e?.response?.data?.detail ||
          extractErrorMessage(e?.response?.data) ||
          t("ERROR_OCCURRED"),
      });
    } finally {
      setRatesSaving(false);
    }
  };

  const updateExchangeRates = async () => {
    const confirmed = window.confirm(t("UPDATE_EXCHANGE_RATES_QUESTION"));
    if (!confirmed) return;
    setLoading(true);
    try {
      await currencyService.updateExchangeRates();
      viewAlert({
        severity: "success",
        message: t("EXCHANGE_RATES_UPDATED"),
      });
      loadCurrencies();
    } catch (e: any) {
      viewAlert({
        severity: "error",
        message:
          e?.response?.data?.detail ||
          extractErrorMessage(e?.response?.data) ||
          t("ERROR_OCCURRED"),
      });
    } finally {
      setLoading(false);
    }
  };

  const updateRelativePrices = async () => {
    const confirmed = window.confirm(t("UPDATE_RELATIVE_PRICES_QUESTION"));
    if (!confirmed) return;
    setLoading(true);
    try {
      await currencyService.updateRelativePrices();
      viewAlert({
        severity: "success",
        message: t("RELATIVE_PRICES_UPDATED"),
      });
      loadCurrencies();
    } catch (e: any) {
      viewAlert({
        severity: "error",
        message:
          e?.response?.data?.detail ||
          extractErrorMessage(e?.response?.data) ||
          t("ERROR_OCCURRED"),
      });
    } finally {
      setLoading(false);
    }
  };

const connectorOptions = useMemo(
  () =>
    (ratesConnectors.all_connectors || []).map((item) => ({
      id: item,
      title: item,
    })),
  [ratesConnectors.all_connectors]
);

  const isRatesStale = useMemo(() => {
    const value = ratesConnectors.last_updated;
    if (!value) return false;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return false;
    const diff = Date.now() - date.getTime();
    return diff > 24 * 60 * 60 * 1000;
  }, [ratesConnectors.last_updated]);

  const formatDate = (value?: string) => {
    if (!value) return "--";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
  };

  const availableCodesPreview = useMemo(() => {
    if (!createOptions?.length) return "";
    const preview = createOptions.slice(0, 5).join(", ");
    const suffix = createOptions.length > 5 ? "…" : "";
    return `${preview}${suffix}`;
  }, [createOptions]);

  if (loading && currencies.length === 0) {
    return <TopProgress loading={loading} />;
  }

  return (
    <>
      <Section style={{ marginBottom: 14 }}>
        <SectionHeader>
          <div>
            <strong>{t("RATES_CONNECTORS")}</strong>
            <Muted>
              {t(
                "RATES_CONNECTORS_DESCRIPTION",
                "Choose the source for exchange rates and toggle automatic updates."
              )}
            </Muted>
          </div>
          <Button
            title={t("SAVE")}
            variant="primary"
            size="mid"
            onClick={saveRatesConnectors}
            disabled={ratesSaving}
            loading={ratesSaving}
          />
        </SectionHeader>
        <RatesWrapper>
          <Toggle
            title={t("AUTO_UPDATE_EXCHANGE_RATES")}
            value={!!ratesConnectors.auto_update}
            onChange={() =>
              setRatesConnectors((prev) => ({
                ...prev,
                auto_update: !prev.auto_update,
              }))
            }
          />
          <Select
            title={t("CONNECTOR")}
            data={connectorOptions}
            selectedValue={ratesConnectors.connector}
            onChange={(val: any) =>
              setRatesConnectors((prev) => ({
                ...prev,
                connector: val?.id || val,
              }))
            }
            placeholder={t("SELECT_CONNECTOR")}
            customStyles={{ minWidth: "220px" }}
            disabled={ratesLoading}
          />
          <LastUpdatedText stale={isRatesStale}>
            {t("LAST_UPDATED")}: {formatDate(ratesConnectors.last_updated)}
          </LastUpdatedText>
        </RatesWrapper>
      </Section>

      <Section>
        <SectionHeader>
          <div>
            <strong>{t("CURRENCIES")}</strong>
            <Muted>
              {t(
                "CURRENCIES_HELP",
                "Manage available currencies and their exchange rates."
              )}
            </Muted>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button
              title={t("CREATE_CURRENCY")}
              variant="primary"
              size="mid"
              onClick={openCreate}
            />
            <Button
              title={t("UPDATE_EXCHANGE_RATES")}
              variant="secondary"
              size="mid"
              onClick={updateExchangeRates}
              disabled={loading}
            />
            <Button
              title={t("UPDATE_RELATIVE_PRICES")}
              variant="secondary"
              size="mid"
              onClick={updateRelativePrices}
              disabled={loading}
            />
          </div>
        </SectionHeader>

        {loading ? (
          <TopProgress loading={loading} />
        ) : (
          <CurrencyList>
            <CurrencyRow>
              <strong>{t("CURRENCY")}</strong>
              <strong>
                {defaultCurrency
                  ? `${t("RATE")} (1 ${defaultCurrency.code})`
                  : t("RATE")}
              </strong>
              <div />
            </CurrencyRow>
            {currencies.map((currency) => (
              <CurrencyRow key={currency.code}>
                <div>
                  {currency.code}{" "}
                  {currency.is_default ? <Pill>{t("DEFAULT")}</Pill> : null}
                </div>
                <div>{currency.is_default ? "1" : currency.rate}</div>
                <Actions>
                  <Button
                    title={t("EDIT")}
                    variant="stroke"
                    size="small"
                    onClick={() => openEdit(currency)}
                  />
                  {!currency.is_default && (
                    <Button
                      title={t("DELETE")}
                      variant="stroke"
                      size="small"
                      isDelete
                      onClick={() => handleDelete(currency)}
                      disabled={currency.is_used}
                    />
                  )}
                </Actions>
              </CurrencyRow>
            ))}
          </CurrencyList>
        )}
      </Section>

      {modalOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.25)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
          }}
          onClick={() => setModalOpen(false)}
        >
          <ModalBody onClick={(e) => e.stopPropagation()}>
            <ModalTitle>
              {editing
                ? t("EDIT_CURRENCY_TITLE", { code: editing.code })
                : t("CREATE_CURRENCY")}
            </ModalTitle>
            <form onSubmit={handleSubmit}>
              <Input
                title={t("CODE")}
                placeholder={t("CODE")}
                fieldName="code"
                handleChange={handleChange}
                handleBlur={handleBlur}
                values={values}
                errors={errors}
                touched={touched}
                disabled={!!editing}
                customStyles={{ textTransform: "uppercase" }}
              />
              {availableCodesPreview && (
                <Muted style={{ marginTop: 6 }}>
                  {t("AVAILABLE_CODES")}: {availableCodesPreview}
                </Muted>
              )}
              <Input
                title={t("RATE")}
                placeholder={t("RATE")}
                fieldName="rate"
                handleChange={handleChange}
                handleBlur={handleBlur}
                values={values}
                errors={errors}
                touched={touched}
                type="number"
              />
              <CheckBox
                title={t("SET_AS_DEFAULT")}
                value={values.is_default || false}
                onChange={() =>
                  setFieldValue("is_default", !values.is_default)
                }
                disabled={!!editing && editing.is_default}
              />

              <ModalFooter>
                <Button
                  title={t("CANCEL")}
                  variant="stroke"
                  size="mid"
                  onClick={() => setModalOpen(false)}
                />
                <Button
                  title={t("SAVE")}
                  variant="primary"
                  size="mid"
                  onClick={() => handleSubmit()}
                  disabled={!isValid || savingCurrency}
                  loading={savingCurrency}
                />
              </ModalFooter>
            </form>
          </ModalBody>
        </div>
      )}
    </>
  );
};

export default Currencies;
